/**
 * Кнопка AI-подсказки рядом с полем в ItemDrawer (M20).
 *
 * Показывает иконку 💡, по клику открывает попап с потоковым
 * ответом AI для конкретного поля/записи. Markdown-рендер.
 */

import { useCallback, useRef, useState } from 'react';
import { Button, Popover, Typography, Space } from 'antd';
import { CloseOutlined, LoadingOutlined, CopyOutlined } from '@ant-design/icons';
import { AiSparkIcon } from '../../shared/components/AiSparkIcon.tsx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AiSuggestKind } from '@lens/shared';
import { streamSuggest, type SseCallbacks } from './api.ts';
import './ai-markdown.css';

const { Text } = Typography;

interface AiSuggestButtonProps {
  itemId: string;
  kind: AiSuggestKind;
  tooltip?: string;
  fieldSlug?: string;
  fieldName?: string;
}

export function AiSuggestButton({ itemId, kind, tooltip, fieldSlug, fieldName }: AiSuggestButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleOpen = useCallback(async () => {
    setOpen(true);
    setContent('');
    setError(null);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    const callbacks: SseCallbacks = {
      onDelta: (chunk) => setContent(prev => prev + chunk),
      onDone: () => {
        setStreaming(false);
        abortRef.current = null;
      },
      onError: (code, msg) => {
        setError(msg ?? code);
        setStreaming(false);
        abortRef.current = null;
      },
    };

    try {
      await streamSuggest(itemId, kind, callbacks, controller.signal, { fieldSlug, fieldName });
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setError('Не удалось получить подсказку');
      }
      setStreaming(false);
    }
  }, [itemId, kind, fieldSlug, fieldName]);

  const handleClose = () => {
    abortRef.current?.abort();
    setOpen(false);
    setStreaming(false);
  };

  const handleCopy = () => {
    void navigator.clipboard.writeText(content);
  };

  const popoverContent = (
    <div style={{ maxWidth: 400, maxHeight: 350, overflow: 'auto' }}>
      {error ? (
        <Text type="danger">{error}</Text>
      ) : content ? (
        <div className="ai-markdown" style={{ fontSize: 13, lineHeight: 1.6 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          {streaming && <LoadingOutlined style={{ marginLeft: 4 }} />}
        </div>
      ) : streaming ? (
        <Space>
          <LoadingOutlined />
          <Text type="secondary">Генерация...</Text>
        </Space>
      ) : null}
      {content && !streaming && (
        <div style={{ marginTop: 8, textAlign: 'right' }}>
          <Button size="small" icon={<CopyOutlined />} onClick={handleCopy}>
            Скопировать
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong style={{ fontSize: 13 }}>Подсказка Lensy</Text>
          <Button size="small" type="text" icon={<CloseOutlined />} onClick={handleClose} />
        </div>
      }
      trigger="click"
      open={open}
      onOpenChange={(v) => { if (!v) handleClose(); }}
    >
      <Button
        type="text"
        size="small"
        icon={<AiSparkIcon />}
        onClick={handleOpen}
        title={tooltip ?? 'Подсказка Lensy'}
        style={{ padding: '0 4px' }}
      />
    </Popover>
  );
}
